import React from "react";
import http from '../modules/api';

import DatePicker from "react-datepicker";
import { ko } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import TextInput from "../modules/common/TextInput";
import SelectBoxV2 from '../modules/common/SelectBoxV2';
import Button from '../modules/common/Button';
import TitleContentDialogV2 from '../modules/dialogs/TitleContentDialogV2';
import SaveDialogV2 from '../modules/dialogs/SaveDialogV2';
import FileDownloadComponent from "../modules/FileDownloadComponent";

import { PreviewRoot } from "../InstagramAutoUpload/component/PreviewRoot_v2";         
import PreviewTrigger from "../InstagramAutoUpload/component/PreviewTrigger_v2";
// import { Button } from "react-bootstrap";

/**
 * AdminInstagramUsageList_v2
 * - instagram_automation_settings_14.html의 테이블 레이아웃/클래스명 기반
 * - DatePicker로 날짜 범위 필터(한국어)
 * - 검색/상태/유형/기간 필터 + 정렬 + 페이징 + 체크박스 선택/일괄 기능
 * - 외부 전역 JS/jQuery 불필요
 *
 * props:
 *  - posts?: PostRow[]
 *  - pageSizes?: number[]        (기본: [20, 50, 100])
 *  - onClickNewPost?(): void
 *  - onView?(row: PostRow): void
 *  - onEdit?(row: PostRow): void
 *  - onDelete?(row: PostRow): void
 *  - onBulkEdit?(ids: string[]): void
 *  - onBulkDelete?(ids: string[]): void
 *
 * PostRow:
 *  {
 *    id: string;
 *    caption: string;
 *    type: '자동' | '수동';
 *    status: '게시됨' | '예약됨' | '임시저장' | '실패';
 *    publishedDate?: string; // 'YYYY.MM.DD'
 *    publishedTime?: string; // 'HH:mm'
 *    reach?: number | null;
 *    reachDeltaPct?: number | null;
 *    likes?: number | null;
 *    comments?: number | null;
 *    saves?: number | null;
 *    thumbnail?: string;     // 이미지 URL
 *    carouselCount?: number; // 캐러셀의 추가 페이지 수 (ex: +3)
 *  }
 */

// 컴포넌트 바깥 또는 클래스 상단에 상수로
const STATUS_OPTIONS = [
  { text: "게시됨",   value: "PUBLISHED" },
  { text: "예약됨",   value: "SCHEDULED" },
  { text: "임시저장", value: "DRAFT" },
  { text: "실패",     value: "FAILED" },
];

const TYPE_OPTIONS = [
  { text: "자동",     value: "Y" },
  { text: "수동",     value: "N" },
];

// TB_INSTAGRAM_MEDIA_ITEM.item_index 예약값: 신규 릴스는 100번 슬롯을 사용한다.
const REELS_ITEM_INDEX = 100;

const clampInstagramAudioVolume = (value, fallback = 100) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(100, Math.round(n)));
};

const parseAudioMetaSnapshot = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  try {
	const parsed = JSON.parse(String(value));
	return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
	return null;
  }
};

const firstAudioText = (...values) => {
  for (const value of values) {
	if (value === null || value === undefined) continue;
	const text = String(value).trim();
	if (text) return text;
  }
  return "";
};

const getAudioPreviewUrlFromMeta = (meta) => {
  const source = meta && typeof meta === "object" ? meta : {};
  return firstAudioText(
	source.download_url,
	source.downloadUrl,
	source.progressive_download_url,
	source.preview_url,
	source.previewUrl,
	source.audio_url,
	source.audioUrl,
	source.url
  );
};

export default class AdminInstagramUsageList_v4 extends React.Component {
  static defaultProps = { pageSizes: [{value: 20, text: "20개"}, {value: 50, text: "50개"}, {value: 100, text: "100개"}] };

  constructor(props) {
	super(props);
	// const sample = this.samplePosts();
	this.state = {
	  // 데이터
	  posts: Array.isArray(props.posts) && props.posts.length ? props.posts : [],
	  serviceUsers: [],
	  user_seq: "",
	  dateType: "all",
	  appliedExcelParams: null,
	  // posts: sample,

	  // 필터/검색
	  q: "",
	  status: "",
	  // type: "",
	  is_auto: "",
	  startDate: null, // DatePicker는 Date 객체
	  endDate: null,

	  // 페이징/정렬/선택
	  page: 1,
	  pageSize: 20,
	  sortKey: "reg_date", // 'caption' | 'reach' | 'likes' | 'comments' | 'saves' | 'publishedAt'
	  sortDir: "desc",        // 'asc' | 'desc'
	  selected: new Set(),

	  // 삭제 다이얼로그
	  deleteDialogOpen: false,
	  deleteTarget: null,
	  deleteInstagramPostChecked: false,
	  deleteProcessing: false,

	  // 로딩, 저장 다이얼로그
	  save_dialog_show_state: false,
	  save_dialog_content: "로딩중 입니다.",
	  save_dialog_button_list: [{ title: "확인", type: "1", display_yn:"N" }],
	};
  }

  componentDidMount() {
	console.log("### AdminInstagramUsageList_v2 ###");
	this.fetchServiceUsers();
	this.fetchPostsFromApi();
  }

  fetchServiceUsers = async () => {
	try {
	  const resp = await http.get("/rest/admin/adminInstagramServiceUserList");
	  this.setState({ serviceUsers: Array.isArray(resp.data) ? resp.data : [] });
	} catch (err) {
	  console.error("인스타그램 서비스 신청 사용자 조회 실패", err);
	  this.setState({ serviceUsers: [] });
	}
  };

  /**
   * API 호출: Spring Boot Instagram Media List
   */
  fetchPostsFromApi = async () => {
	const { user_seq, status, is_auto, q, startDate, endDate, page, pageSize, sortKey, sortDir } = this.state;
	const formatDate = (date) => {
	  if (!date) return null;
	  const year = date.getFullYear();
	  const month = String(date.getMonth() + 1).padStart(2, '0');
	  const day = String(date.getDate()).padStart(2, '0');
	  return `${year}-${month}-${day}`;
	};
	const formatDateTime = (value) => {
	  if (!value) return { date: "", time: "" };
	  const parsed = new Date(value);
	  if (Number.isNaN(parsed.getTime())) {
		const text = String(value);
		return { date: text.substring(0, 10).replace(/-/g, "."), time: text.substring(11, 16) };
	  }
	  const kst = new Date(parsed.getTime() + 9 * 60 * 60 * 1000);
	  const iso = kst.toISOString();
	  return { date: iso.substring(0, 10).replace(/-/g, "."), time: iso.substring(11, 16) };
	};

	const requestParams = {
	  user_seq,
	  status: status === "DRAFT" ? "" : status === "SCHEDULED" ? "READY" : status,
	  is_auto,
	  post_type: status === "SCHEDULED" ? "SCHEDULED" : "",
	  q,
	  is_draft: status === "SCHEDULED" ? "N" : status === "DRAFT" ? "Y" : "",
	  startDate: startDate ? formatDate(startDate) : null,
	  endDate: endDate ? formatDate(endDate) : null,
	  sortKey,
	  sortDir: String(sortDir || "desc").toUpperCase(),
	  start: (page - 1) * pageSize,
	  rowCnt: pageSize,
	};

	try {
	  const resp = await http.get("/rest/admin/adminInstagramUsageMediaList", {
		params: requestParams,
	  });

	  const { total, list } = resp.data || {};
	  const posts = (list || []).map((m) => {
		const mediaTypeCd = String(m.media_type_cd || "").toUpperCase();

		const audioMeta = parseAudioMetaSnapshot(m.audio_meta_snapshot_json);
		const normalizedAudioType = String(m.instagram_audio_type || "").toUpperCase();
		let audioMode = String(m.audio_mode_cd || "NONE").toUpperCase();
		if (!["MUSIC", "ORIGINAL_SOUND"].includes(audioMode)) {
		  audioMode = m.instagram_audio_id && ["MUSIC", "ORIGINAL_SOUND"].includes(normalizedAudioType)
			? normalizedAudioType
			: "NONE";
		}

		const storedReelsAudio = {
		  mode: audioMode,
		  audioId: m.instagram_audio_id ? String(m.instagram_audio_id) : "",
		  audioType: normalizedAudioType || audioMode,
		  title: m.instagram_audio_title || "",
		  artist: m.instagram_audio_artist || "",
		  durationMs: m.instagram_audio_duration_ms !== null && m.instagram_audio_duration_ms !== undefined
			? Number(m.instagram_audio_duration_ms)
			: null,
		  previewUrl: getAudioPreviewUrlFromMeta(audioMeta),
		  audioVolume: clampInstagramAudioVolume(m.audio_volume, 100),
		  videoVolume: clampInstagramAudioVolume(m.video_volume, 100),
		  audioName: m.audio_name || "",
		  mediaAudioType: m.media_audio_type || "",
		  meta: audioMeta,
		};

		// 임시저장에는 비활성 릴스 음악도 DB에 보존될 수 있다.
		// 관리자 목록/미리보기에서는 현재 콘텐츠가 릴스(R)일 때만 음악을 노출한다.
		const reelsAudio = mediaTypeCd === "R"
		  ? storedReelsAudio
		  : {
			  ...storedReelsAudio,
			  mode: "NONE",
			  audioId: "",
			  audioType: "NONE",
			  title: "",
			  artist: "",
			  durationMs: null,
			  previewUrl: "",
			  audioName: "",
			  mediaAudioType: "",
			  meta: null,
			};
		// 파일 생성/유입 방식은 자동화 종류가 아니라 TB_MEDIA_SOURCE.source_type_cd 기준으로 표시한다.
		// A=AI 이미지 스튜디오, T=AI 디자인(템플릿), U=파일 업로드, P=상품 이미지.
		const fallbackFileType = m.fallback_content_source_type || "기타";
		const normalizeFileType = (value) => value || "기타";
		const allItems = (m.items || [])
		  .slice()
		  .sort((a, b) => (a.item_index ?? 0) - (b.item_index ?? 0));

		const getItemIndex = (item) => {
		  const index = Number(item?.item_index);
		  return Number.isFinite(index) ? index : null;
		};
		const hasReservedReelsItem = allItems.some((item) => getItemIndex(item) === REELS_ITEM_INDEX);

		// 신규 구조: R은 100번 슬롯만, S/C는 100번을 제외한다.
		// 기존 릴스(R + item_index 0)는 100번이 없을 때 legacy fallback으로 그대로 노출한다.
		const orderedItems = allItems.filter((item) => {
		  const itemIndex = getItemIndex(item);
		  if (mediaTypeCd === "R") {
			return hasReservedReelsItem
			  ? itemIndex === REELS_ITEM_INDEX
			  : itemIndex !== REELS_ITEM_INDEX;
		  }
		  return itemIndex !== REELS_ITEM_INDEX;
		});

		// 피드별 파일 저장 방식 개수. 캐러셀은 TB_INSTAGRAM_MEDIA_ITEM 각 아이템을 1건으로 집계한다.
		const fileTypeCounts = {
		  aiImageStudio: 0,
		  aiDesign: 0,
		  upload: 0,
		  product: 0,
		  video: 0,
		  other: 0,
		};
		const countFileType = (type) => {
		  if (type === "동영상") fileTypeCounts.video += 1;
		  else if (type === "AI 이미지 스튜디오") fileTypeCounts.aiImageStudio += 1;
		  else if (type === "AI 디자인") fileTypeCounts.aiDesign += 1;
		  else if (type === "파일 업로드") fileTypeCounts.upload += 1;
		  else if (type === "상품 이미지") fileTypeCounts.product += 1;
		  else fileTypeCounts.other += 1;
		};

		orderedItems.forEach((it) => {
		  countFileType(normalizeFileType(it.content_source_type || fallbackFileType));
		});

		const slides = orderedItems
		  .map((it) => {
			const sourceUrl = it.source_url || "";
			const mediaUrl = it.media_url || "";
			const finalUrl = mediaUrl || sourceUrl;
			const fileTypeBadge = normalizeFileType(it.content_source_type || fallbackFileType);
			const isVideo =
			  fileTypeBadge === "동영상" ||
			  mediaTypeCd === "R" ||
			  String(it.media_mime || "").toLowerCase().startsWith("video/") ||
			  this.looksLikeVideoUrl(finalUrl);

			const rawPreviewUrl = m.thumbnail_url || finalUrl || "";
			const safeThumbnail = rawPreviewUrl && !this.looksLikeVideoUrl(rawPreviewUrl)
			  ? rawPreviewUrl
			  : "";

			return {
			  type: isVideo ? "video" : "image",
			  url: finalUrl || safeThumbnail,
			  thumbnail: safeThumbnail,
			  fileTypeBadge,
			};
		  })
		  .filter((slide) => slide.url || slide.thumbnail);

		if (!slides.length && m.thumbnail_url) {
		  const fallbackType = normalizeFileType(fallbackFileType);
		  slides.push({
			type: mediaTypeCd === "R" || this.looksLikeVideoUrl(m.thumbnail_url) ? "video" : "image",
			url: m.thumbnail_url,
			thumbnail: this.looksLikeVideoUrl(m.thumbnail_url) ? "" : m.thumbnail_url,
			fileTypeBadge: fallbackType,
		  });
		  if (!orderedItems.length) countFileType(fallbackType);
		}

		const sourceTypes = Array.from(new Set(slides.map((slide) => slide.fileTypeBadge).filter(Boolean)));
		const isVideoPost = mediaTypeCd === "R" || slides.some((slide) => slide.type === "video");
		const coverCandidates = [m.thumbnail_url, slides[0]?.thumbnail, slides[0]?.url].filter(Boolean);
		const cover = coverCandidates.find((url) => !this.looksLikeVideoUrl(url)) || "";
		const published = formatDateTime(m.published_time || m.scheduled_time);
		const registered = formatDateTime(m.reg_date);

		return {
		  id: String(m.instagram_media_seq),
		  userSeq: m.user_seq,
		  userId: m.user_id || "",
		  corpName: m.corp_name || "",
		  userRegDate: m.user_reg_date || "",
		  freeRemainDate: m.ai_instagram_free_remain_date,
		  payRemainDate: m.ai_instagram_pay_remain_date,
		  caption: m.caption,
		  username: m.username || "",
		  facebook_username: m.facebook_username || "",

		  // v2: Instagram / Threads 발행 플랫폼 정보
		  // 신규 백엔드 필드가 없을 때는 기존 Instagram 목록 동작을 유지한다.
		  instagramPublishYn: String(m.instagram_publish_yn ?? "Y").toUpperCase(),
		  threadsPublishYn: String(
			m.threads_publish_yn ?? (m.threads_media_seq || m.threads_media_id ? "Y" : "N")
		  ).toUpperCase(),
		  threadsUsername:
			m.threads_username || m.threads_account_username || m.thread_username || m.threads_user_id || "",
		  threadsMediaSeq: m.threads_media_seq ?? null,
		  threadsMediaId: m.threads_media_id || null,
		  threadsPublishStatusCd: m.threads_publish_status_cd || m.threads_status || null,
		  threadsPermalink: m.threads_permalink || null,
		  status:
			m.status === "PUBLISHED" ? "게시됨" :
			m.status === "FAILED" ? "실패" :
			m.is_draft === "Y" ? "임시저장" :
			m.post_type === "SCHEDULED" ? "예약됨" : m.status,
		  publishStatusText: m.publish_status_name || (m.status === "PUBLISHED" ? "배포" : "미배포"),
		  publishedDate: published.date,
		  publishedTime: published.time,
		  regDate: registered.date,
		  regTime: registered.time,
		  view_cnt: m.view_cnt ?? null,
		  like_cnt: m.like_cnt ?? null,
		  comment_cnt: m.comment_cnt ?? null,
		  share_cnt: m.share_cnt ?? null,

		  // v2: Threads 참여 지표. 현재 관리자 API가 내려주는 alias를 폭넓게 허용한다.
		  threads_like_cnt: m.threads_like_cnt ?? m.threads_like_count ?? m.threads_likes ?? null,
		  threads_reply_cnt:
			m.threads_reply_cnt ?? m.threads_reply_count ?? m.threads_replies ?? m.threads_comment_cnt ?? null,
		  threads_view_cnt: m.threads_view_cnt ?? m.threads_view_count ?? m.threads_views ?? null,
		  threadsPublishedDate: formatDateTime(
			m.threads_published_at || m.threads_published_time ||
			(m.post_type === "SCHEDULED" && String(m.threads_publish_yn || "").toUpperCase() === "Y" ? m.scheduled_time : null)
		  ).date,
		  threadsPublishedTime: formatDateTime(
			m.threads_published_at || m.threads_published_time ||
			(m.post_type === "SCHEDULED" && String(m.threads_publish_yn || "").toUpperCase() === "Y" ? m.scheduled_time : null)
		  ).time,
		  thumbnail: cover,
		  carouselCount: slides.length > 1 ? (slides.length - 1) : null,
		  slides,
		  sourceTypes,
		  fileTypeCounts,
		  isVideoPost,
		  reelsAudio,
		  musicCount: mediaTypeCd === "R" && reelsAudio.audioId ? 1 : 0,
		  hashtags: m.hashtags,
		  isAuto: m.is_auto,
		  isAutoText: m.is_auto === "Y" ? "자동" : "수동",
		  autoSettingType: m.auto_setting_type || "",
		  instagramAiImageAutoSettingSeq: m.instagram_ai_image_auto_setting_seq || null,
		  instagramProductAutoSettingSeq: m.instagram_product_auto_setting_seq || null,
		  image_ratio: m.image_ratio || (isVideoPost ? "9/16" : "1/1"),
		  permalink: m.permalink || "",
		};
	  });

	  this.setState({
		posts,
		total: Number(total || 0),
		appliedExcelParams: {
		  user_seq: requestParams.user_seq || "",
		  status: requestParams.status || "",
		  is_auto: requestParams.is_auto || "",
		  post_type: requestParams.post_type || "",
		  q: requestParams.q || "",
		  is_draft: requestParams.is_draft || "",
		  startDate: requestParams.startDate || "",
		  endDate: requestParams.endDate || "",
		  sortKey: requestParams.sortKey || "reg_date",
		  sortDir: requestParams.sortDir || "DESC",
		},
	  });
	} catch (err) {
	  console.error("관리자 인스타그램 이용 현황 조회 실패", err);
	  this.setState({ posts: [], total: 0 });
	}
  };

  // 샘플 데이터 (주입 없을 때)
  samplePosts() {
	return [
	  {
		id: "1",
		caption: "겨울 신상품 입고! 🎉",
		type: "자동",
		status: "게시됨",
		publishedDate: "2024.01.15",
		publishedTime: "14:00",
		reach: 1234,
		reachDeltaPct: 15.2,
		likes: 89,
		comments: 12,
		saves: 5,
		thumbnail: "", // 없으면 placeholder
	  },
	  {
		id: "2",
		caption: "오늘의 데일리룩 추천 👗",
		type: "자동",
		status: "게시됨",
		publishedDate: "2024.01.14",
		publishedTime: "18:30",
		reach: 2456,
		reachDeltaPct: 32.1,
		likes: 156,
		comments: 23,
		saves: 12,
		thumbnail: "",
		carouselCount: 3,
	  },
	  {
		id: "3",
		caption: "주말 특가! 전 상품 30% 할인 🛍️",
		type: "수동",
		status: "예약됨",
		publishedDate: "2024.01.20",
		publishedTime: "10:00",
		reach: null,
		reachDeltaPct: null,
		likes: null,
		comments: null,
		saves: null,
		thumbnail: "",
	  },
	  {
		id: "4",
		caption: "권한 문제로 발행 실패",
		type: "자동",
		status: "실패",
		publishedDate: "2024.01.13",
		publishedTime: "12:00",
		reach: null,
		reachDeltaPct: null,
		likes: 0,
		comments: 2,
		saves: 0,
		thumbnail: "",
	  },
	];
  }

  // 유틸
  looksLikeVideoUrl = (url) => {
	const value = String(url || "").split("?")[0].toLowerCase();
	return /\.(mp4|mov|m4v|webm|avi|mpeg|mpg)$/.test(value);
  };

  formatDateForApi = (date) => {
	if (!date) return "";
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
  };

  getExcelDownloadUrl = () => {
	const applied = this.state.appliedExcelParams;
	const currentStatus = this.state.status;
	const paramsSource = applied || {
	  user_seq: this.state.user_seq || "",
	  status: currentStatus === "DRAFT" ? "" : currentStatus === "SCHEDULED" ? "READY" : currentStatus,
	  is_auto: this.state.is_auto || "",
	  post_type: currentStatus === "SCHEDULED" ? "SCHEDULED" : "",
	  q: this.state.q || "",
	  is_draft: currentStatus === "SCHEDULED" ? "N" : currentStatus === "DRAFT" ? "Y" : "",
	  startDate: this.formatDateForApi(this.state.startDate),
	  endDate: this.formatDateForApi(this.state.endDate),
	  sortKey: this.state.sortKey || "reg_date",
	  sortDir: String(this.state.sortDir || "desc").toUpperCase(),
	};

	return "/rest/admin/adminInstagramUsageMediaList/excel?" + Object.keys(paramsSource)
	  .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(paramsSource[key] ?? "")}`)
	  .join("&");
  };

  fmtNumber(n) { return (n === null || n === undefined) ? "-" : n.toLocaleString(); }
  
  // 이벤트
  changeSort = (key) => {
	this.setState((prev) =>
	  prev.sortKey === key
		? { sortDir: prev.sortDir === "asc" ? "desc" : "asc" }
		: { sortKey: key, sortDir: "desc" }
	, this.fetchPostsFromApi);
  };
  handleInput = (e) => {
	const { name, value } = e.target;
	this.setState({ [name]: value, page: 1 });
  };
  setQuickRange = (type) => {
	const today = new Date();
	if (type === "today") {
	  this.setState({ startDate: today, endDate: today, page: 1 }, this.fetchPostsFromApi);
	} else if (type === "week") {
	  const day = today.getDay();                 // 0=일
	  const diff = day === 0 ? 6 : day - 1;       // 월요일 시작
	  const monday = new Date(today); monday.setDate(today.getDate() - diff);
	  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
	  this.setState({ startDate: monday, endDate: sunday, page: 1 }, this.fetchPostsFromApi);
	} else if (type === "month") {
	  const first = new Date(today.getFullYear(), today.getMonth(), 1);
	  const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
	 this.setState({ startDate: first, endDate: last, page: 1 }, this.fetchPostsFromApi);
	} else {
	  // 전체(초기화)
	  this.setState({ startDate: null, endDate: null, page: 1 }, this.fetchPostsFromApi);
	}

	this.setState({ dateType: type });
  };

  // 선택/일괄
  toggleSelectAll = (e, rowsOnPage) => {
	const checked = e.target.checked;
	this.setState((prev) => {
	  const next = new Set(prev.selected);
	  if (checked) rowsOnPage.forEach((r) => next.add(r.id));
	  else rowsOnPage.forEach((r) => next.delete(r.id));
	  return { selected: next };
	});
  };
  toggleSelectOne = (id) => {
	this.setState((prev) => {
	  const next = new Set(prev.selected);
	  next.has(id) ? next.delete(id) : next.add(id);
	  return { selected: next };
	});
  };

  goPage = (p) => this.setState({ page: p }, this.fetchPostsFromApi);
  changePageSize = (e) => this.setState({ pageSize: Number(e.target.value), page: 1 }, this.fetchPostsFromApi);

  onView = (row) => this.props.onView && this.props.onView(row);
  onEdit = (row) => this.props.onEdit && this.props.onEdit(row);
  openDeleteDialog = (row, e) => {
	if (e) {
	  e.preventDefault();
	  e.stopPropagation();
	}

	this.setState({
	  deleteDialogOpen: true,
	  deleteTarget: row,
	  deleteInstagramPostChecked: false,
	  deleteProcessing: false,
	});
  };

  closeDeleteDialog = () => {
	if (this.state.deleteProcessing) return;

	this.setState({
	  deleteDialogOpen: false,
	  deleteTarget: null,
	  deleteInstagramPostChecked: false,
	});
  };

  handleDeleteInstagramCheck = (eventOrChecked, checkedValue) => {
	let checked = checkedValue;

	if (typeof eventOrChecked === "boolean") {
	  checked = eventOrChecked;
	} else if (eventOrChecked && eventOrChecked.target) {
	  checked = eventOrChecked.target.checked;
	}

	this.setState({ deleteInstagramPostChecked: Boolean(checked) });
  };

  getDeleteDialogContent = () => {
	const row = this.state.deleteTarget;
	if (!row) return "";

	if (row.status === "게시됨") {
	  return "인스타그램에 이미 발행된 게시물입니다.<br/>실제 인스타그램 게시물도 함께 삭제하려면 아래 항목을 선택해 주세요.<br/><br/><strong>선택하지 않으면 로보엠디 목록에서만 삭제됩니다.</strong>";
	}
	if (row.status === "예약됨") {
	  return "예약 게시물을 삭제하면 예정된 시간에 게시되지 않습니다.<br/>삭제한 게시물은 복구할 수 없습니다.";
	}
	if (row.status === "임시저장") {
	  return "임시저장 게시물을 목록에서 삭제합니다.<br/>삭제한 게시물은 복구할 수 없습니다.";
	}
	if (row.status === "실패") {
	  return "발행에 실패한 게시물을 목록에서 삭제합니다.<br/>삭제한 게시물은 복구할 수 없습니다.";
	}

	return "게시물을 목록에서 삭제합니다.<br/>삭제한 게시물은 복구할 수 없습니다.";
  };

  deletePost = async () => {
	const { deleteTarget, deleteInstagramPostChecked, deleteProcessing } = this.state;
	if (!deleteTarget || deleteProcessing) return;

	const isPublished = deleteTarget.status === "게시됨";
	const deleteInstagramToo = isPublished && deleteInstagramPostChecked;
	const deleteUrl = deleteInstagramToo
	  ? `/rest/shop/instagram/media/${deleteTarget.id}`
	  : `/rest/shop/instagram/media/${deleteTarget.id}/db-only`;

	this.setState({ deleteProcessing: true });
	this.setState({ save_dialog_show_state: true, save_dialog_content: "삭제 중 입니다...", save_dialog_button_list: [{ title: "확인", type: "1", display_yn:"N" }] });

	try {
	  const response = await http.delete(deleteUrl);
	  const data = response && response.data ? response.data : {};

	  if (data.status && data.status !== "success") {
		throw new Error(data.message || "게시물 삭제에 실패했습니다.");
	  }

	  this.setState({
		deleteDialogOpen: false,
		deleteTarget: null,
		deleteInstagramPostChecked: false,
		deleteProcessing: false,
		selected: new Set(),
	  }, async () => {
		await this.fetchPostsFromApi();
		// window.alert(deleteInstagramToo
		  // ? "Instagram 게시물과 목록 데이터를 삭제했습니다."
		  // : "목록에서 게시물을 삭제했습니다.");

		this.setState({ save_dialog_show_state: true, save_dialog_content: deleteInstagramToo ? "인스타그램 게시물과 목록 데이터를 삭제했습니다." : "목록에서 게시물을 삭제했습니다.", save_dialog_button_list: [{ title: "확인", type: "1", display_yn:"Y", event_method: () => this.setState({ save_dialog_show_state: false }) }] });
	  });
	} catch (err) {
	  console.error("게시물 삭제 실패", err);
	  const message = err?.response?.data?.message || err?.message || "게시물 삭제에 실패했습니다.";
	  this.setState({ deleteProcessing: false }, () => window.alert(message));
	}
  };

  onDelete = (row, e) => this.openDeleteDialog(row, e);
  onBulkEdit = () => this.props.onBulkEdit && this.props.onBulkEdit(Array.from(this.state.selected));
  onBulkDelete = () => this.props.onBulkDelete && this.props.onBulkDelete(Array.from(this.state.selected));

  renderSortHead(label, key, align = "left", width = "auto") {
	const { sortKey, sortDir } = this.state;
	const active = sortKey === key;
	const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
	return (
	  <th onClick={() => this.changeSort(key)} style={{ width: width, cursor: "pointer", textAlign: align }} title="정렬">
		{label} {arrow}
	  </th>
	);
  }

  // 클래스 컴포넌트 내부 메서드(기존 handleInput 사용과 호환)
  handleSelectBox = (name) => (text, value /*, id*/) => {
	// 기존 this.handleInput(e) 로직과 맞추기 위해 synthetic event 형태로 호출
	this.handleInput({ target: { name, value } });
  };

  formatRemainDays = (value, expiredText) => {
	if (value === null || value === undefined || value === "") return "-";
	const days = Number(value);
	if (Number.isNaN(days)) return "-";
	if (days < 0) return expiredText || `D+${Math.abs(days)}일`;
	return `D-${days}일`;
  };

  renderListPageStyles() {
	return (
	  <style>{`
		.instagram-post-page {
		  --primary-mint: #00e2e6;
		  --primary-dark: #00bfc2;
		  --navy-dark: #0A1A2F;
		  --bg-body: #f4f7f9;
		  --bg-canvas: #ffffff;
		  --text-primary: #111827;
		  --text-secondary: #4b5563;
		  --text-muted: #9ca3af;
		  --border-color: #e5e7eb;
		  --border-light: #f3f4f6;
		  background-color: var(--bg-body);
		  color: var(--text-primary);
		  display: flex;
		  min-height: 100vh;
		  overflow-x: hidden;
		  width: 100%;
		  letter-spacing: 0;
		  padding-top: 72px !important;
		  max-width: 100% !important;
		  min-width: 100% !important;
		}

		.instagram-post-page * {
		  box-sizing: border-box;
		  font-family: 'Noto Sans KR', sans-serif;
		  letter-spacing: 0;
		}

		.instagram-post-page f-s-12 {
		  font-size: 12px;
		}
	   
		.instagram-post-page f-s-13 {
		  font-size: 13px;
		}

		.instagram-post-page f-w-900 {
		  font-weight: 900;
		}

		.instagram-post-page button,
		.instagram-post-page input {
		  font-family: inherit;
		}

		.instagram-post-page .btn {
		  display: inline-flex;
		  align-items: center;
		  justify-content: center;
		  gap: 8px;
		  border: none;
		  border-radius: 8px;
		  font-size: 15px;
		  font-weight: 700;
		  cursor: pointer;
		  transition: all 0.2s ease;
		  // padding: 14px 28px;
		  padding: 0px 0px !important;
		}
		.instagram-post-page .btn_mint {
		  background: var(--primary-mint);
		  color: var(--navy-dark);
		  box-shadow: 0 4px 16px rgba(0, 226, 230, 0.35);
		}
		.instagram-post-page .btn_mint:hover {
			background: var(--primary-dark)!important;
			transform: translateY(-2px)!important;
			box-shadow: 0 6px 20px rgba(0, 226, 230, 0.45)!important;
		}

		.instagram-post-page .main-content {
		  display: flex;
		  flex: 1;
		  flex-direction: column;
		  min-width: 0;
		  width: 100%;
		}

		.instagram-post-page .content-inner {
		  display: flex;
		  flex: 1;
		  flex-direction: column;
		  gap: 32px;
		  margin: 0 auto;
		  max-width: 1600px;
		  padding: 32px 40px;
		  width: 100%;
		  min-height: calc(100vh - 72px);
		}

		.instagram-post-page .center-hero {
		  align-items: center;
		  display: flex;
		  flex-direction: column;
		  gap: 12px;
		  margin-bottom: 8px;
		  text-align: center;
		}

		.instagram-post-page .center-hero h2 {
		  color: var(--navy-dark);
		  font-size: 22px;
		  font-weight: 800;
		  margin: 0;
		}

		.instagram-post-page .center-hero p {
		  color: var(--text-secondary);
		  font-size: 14px;
		  line-height: 1.5;
		  margin: 0 0 12px;
		  max-width: 800px;
		  word-break: keep-all;
		}

		.instagram-post-page .hero-action {
		  align-items: center;
		  display: inline-flex;
		  gap: 8px;
		}

		.instagram-post-page .list-section-wrapper {
		  background: var(--bg-canvas);
		  border: 1px solid var(--border-color);
		  border-radius: 12px;
		  box-shadow: 0 2px 8px rgba(10, 26, 47, 0.02);
		  overflow: hidden;
		}

		.instagram-post-page .filter-header {
		  border-bottom: 1px solid var(--border-color);
		  display: flex;
		  flex-direction: column;
		  gap: 16px;
		  padding: 20px;
		}

		.instagram-post-page .filter-controls-row {
		  align-items: center;
		  display: flex;
		  flex-wrap: wrap;
		  gap: 12px;
		}

		.instagram-post-page .search-box-unified {
		  align-items: center;
		  background: var(--bg-canvas);
		  border: 1px solid var(--border-color);
		  border-radius: 6px;
		  display: flex;
		  flex: 2;
		  min-width: 250px;
		  padding: 0 14px;
		  transition: border-color 0.2s;
		}

		.instagram-post-page .search-box-unified:focus-within {
		  border-color: var(--primary-mint);
		}

		.instagram-post-page .fa {
		  font-family: FontAwesome !important;
		  font-style: normal;
		  font-weight: normal;
		}

		.instagram-post-page .search-icon {
		  color: var(--text-muted);
		  font-size: 14px;
		  flex-shrink: 0;
		}
		
		.instagram-post-page #search_q {
		  border: none;
		}
		
		.instagram-post-page .select_area {
		  border-color: var(--border-color) !important;
		  border-radius: 6px !important;
		}

		.instagram-post-page .react-datepicker-wrapper {
		  margin-bottom: 0 !important;
		}

		.instagram-post-page .filter-select-wrap {
		  flex: 1;
		  min-width: 130px;
		}

		.instagram-post-page .date-range-group {
		  align-items: center;
		  display: flex;
		  flex: 1.5;
		  gap: 8px;
		  min-width: 290px;
		}

		.instagram-post-page .date-input {
		  border: 1px solid var(--border-color);
		  border-radius: 6px;
		  cursor: pointer;
		  font-size: 13px;
		  outline: none;
		  padding: 10px 14px;
		  text-align: center;
		  width: 100%;
		}

		.instagram-post-page .date-picker-wrapper {
		  flex: 1;
		}

		.instagram-post-page .btn-apply {
		  background: #64748b;
		  border: none;
		  border-radius: 6px;
		  color: #fff;
		  cursor: pointer;
		  font-size: 13px;
		  font-weight: 600;
		  padding: 10px 24px;
		  transition: all 0.2s;
		  white-space: nowrap;
		}

		.instagram-post-page .btn-apply:hover {
		  background: var(--navy-dark);
		}

		.instagram-post-page .filter-chips-row {
		  align-items: center;
		  display: flex;
		  flex-wrap: wrap;
		  gap: 12px;
		  justify-content: space-between;
		}

		.instagram-post-page .chip-group {
		  display: flex;
		  flex-wrap: wrap;
		  gap: 6px;
		}

		.instagram-post-page .chip-btn {
		  background: var(--bg-canvas);
		  border: 1px solid var(--border-color);
		  border-radius: 20px;
		  color: var(--text-secondary);
		  cursor: pointer;
		  font-size: 12px;
		  font-weight: 600;
		  padding: 6px 14px;
		  transition: all 0.2s;
		  white-space: nowrap;
		}

		.instagram-post-page .chip-btn:hover:not(.active) {
		  background: var(--border-light);
		}

		.instagram-post-page .chip-btn.active {
		  background: #e2e8f0;
		  border-color: #cbd5e1;
		  color: #1e293b;
		  font-weight: 700;
		}

		.instagram-post-page .total-count {
		  color: var(--text-secondary);
		  font-size: 13px;
		  white-space: nowrap;
		}

		.instagram-post-page .total-count span {
		  color: var(--navy-dark);
		  font-weight: 700;
		}

		.instagram-post-page .table-responsive {
		  overflow-x: auto;
		  width: 100%;
		}

		.instagram-post-page .robo-table {
		  border-collapse: collapse;
		  min-width: 1360px;
		  text-align: center;
		  width: 100%;
		}

		.instagram-post-page .robo-table th {
		  background: #f8fafc;
		  border-bottom: 1px solid var(--border-color);
		  color: var(--navy-dark);
		  font-size: 13px;
		  font-weight: 700;
		  padding: 16px 12px;
		  white-space: nowrap;
		}

		.instagram-post-page .robo-table td {
		  border-bottom: 1px solid var(--border-light);
		  color: var(--text-secondary);
		  font-size: 13px;
		  padding: 16px 12px;
		  vertical-align: middle;
		}

		.instagram-post-page .robo-table tr:hover {
		  background: #f8fafc;
		}

		.instagram-post-page .robo-table .text-left {
		  text-align: left;
		}

		.instagram-post-page .cell-account {
		  color: var(--text-primary);
		  line-height: 1.4;
		  word-break: keep-all;
		}

		.instagram-post-page .cell-account span {
		  color: var(--text-muted);
		  display: block;
		  font-size: 12px;
		  margin-top: 2px;
		}

		.instagram-post-page .cell-link {
		  color: var(--primary-dark);
		  display: inline-block;
		  font-weight: 600;
		  line-height: 1.4;
		  max-width: 190px;
		  overflow: hidden;
		  text-decoration: none;
		  text-overflow: ellipsis;
		  white-space: nowrap;
		  word-break: keep-all;
		}

		.instagram-post-page .robo-table td a.cell-link {
		  color: var(--primary-dark) !important;
		  cursor: pointer;
		  text-decoration: none !important;
		  transition: color 0.15s ease;
		}

		.instagram-post-page .robo-table td a.cell-link:hover,
		.instagram-post-page .robo-table td a.cell-link:focus-visible {
		  // color: #008f91 !important;
		  text-decoration: underline !important;
		  // text-decoration-thickness: 1px;
		  // text-underline-offset: 3px;
		}

		.instagram-post-page .list-no {
		  color: var(--text-secondary);
		  font-weight: 600;
		}

		.instagram-post-page .thumb-img,
		.instagram-post-page .thumb-media {
		  border: 1px solid var(--border-color);
		  border-radius: 8px;
		  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
		  height: 64px;
		  width: 64px;
		}

		.instagram-post-page .thumb-img {
		  display: block;
		  object-fit: cover;
		}

		.instagram-post-page .thumb-media {
		  background: #111827;
		  overflow: hidden;
		  position: relative;
		}

		.instagram-post-page .thumb-media img {
		  display: block;
		  height: 100%;
		  object-fit: cover;
		  width: 100%;
		}

		.instagram-post-page .thumb-placeholder {
		  background: linear-gradient(135deg, #667eea, #764ba2);
		}

		.instagram-post-page .thumb-video-dim {
		  position: absolute;
		  inset: 0;
		  background: rgba(0,0,0,0.18);
		  pointer-events: none;
		}

		.instagram-post-page .thumb-play-icon {
		  position: absolute;
		  top: 50%;
		  left: 50%;
		  width: 28px;
		  height: 28px;
		  transform: translate(-50%, -50%);
		  border-radius: 50%;
		  background: rgba(255,255,255,0.92);
		  display: flex;
		  align-items: center;
		  justify-content: center;
		  pointer-events: none;
		  box-shadow: 0 3px 8px rgba(0,0,0,0.25);
		}

		.instagram-post-page .thumb-play-icon span {
		  margin-left: 2px;
		  width: 0;
		  height: 0;
		  border-top: 7px solid transparent;
		  border-bottom: 7px solid transparent;
		  border-left: 10px solid #111827;
		}

		.instagram-post-page .thumb-count-badge {
		  position: absolute;
		  right: 5px;
		  top: 5px;
		  padding: 2px 6px;
		  border-radius: 999px;
		  background: rgba(17, 24, 39, 0.78);
		  color: #fff;
		  font-size: 10px;
		  font-weight: 700;
		  line-height: 1.2;
		}

		.instagram-post-page .caption-text {
		  color: var(--text-primary);
		  font-size: 14px;
		  font-weight: 500;
		  line-height: 1.5;
		  word-break: break-all;
		}

		.instagram-post-page .badge {
		  align-items: center;
		  border-radius: 20px;
		  display: inline-flex;
		  font-size: 12px;
		  font-weight: 700;
		  gap: 4px;
		  justify-content: center;
		  padding: 5px 12px;
		  white-space: nowrap;
		}

		.instagram-post-page .badge-mint {
		  background: rgba(0, 226, 230, 0.15);
		  color: #008f91;
		}

		.instagram-post-page .badge-gray {
		  background: #f1f5f9;
		  color: #475569;
		}

		.instagram-post-page .badge-blue {
		  background: #e0f2fe;
		  color: #0369a1;
		}

		.instagram-post-page .badge-red {
		  background: #fee2e2;
		  color: #b91c1c;
		}

		.instagram-post-page .badge-yellow {
		  background: #fef3c7;
		  color: #92400e;
		}

		.instagram-post-page .cell-date {
		  color: var(--text-primary);
		  font-weight: 500;
		  line-height: 1.4;
		}

		.instagram-post-page .cell-date span {
		  color: var(--text-muted);
		  display: block;
		  font-size: 12px;
		  font-weight: 400;
		  margin-top: 2px;
		}

		.instagram-post-page .engagement-box {
		  align-items: center;
		  color: var(--text-secondary);
		  display: flex;
		  font-size: 13px;
		  gap: 12px;
		  justify-content: center;
		}

		.instagram-post-page .engagement-box.active {
		  color: var(--text-primary);
		  font-weight: 700;
		}

		.instagram-post-page .engagement-item {
		  align-items: center;
		  display: flex;
		  gap: 4px;
		}

		.instagram-post-page .engagement-heart {
		  color: #ef4444;
		}

		.instagram-post-page .engagement-comment,
		.instagram-post-page .engagement-view {
		  color: #64748b;
		}

		.instagram-post-page .pagination-container {
		  align-items: center;
		  display: flex;
		  flex-wrap: wrap;
		  gap: 16px;
		  justify-content: space-between;
		  padding: 16px 20px;
		}

		.instagram-post-page .page-numbers {
		  display: flex;
		  gap: 4px;
		  margin: 0 auto;
		}

		.instagram-post-page .page-numbers button {
		  align-items: center;
		  background: var(--bg-canvas);
		  border: 1px solid var(--border-color);
		  border-radius: 4px;
		  color: var(--text-secondary);
		  cursor: pointer;
		  display: inline-flex;
		  font-size: 13px;
		  font-weight: 500;
		  height: 32px;
		  justify-content: center;
		  transition: all 0.2s;
		  width: 32px;
		}

		.instagram-post-page .page-numbers button:hover:not(:disabled) {
		  background: var(--border-light);
		}

		.instagram-post-page .page-numbers button.active {
		  background: #e2e8f0;
		  border-color: #cbd5e1;
		  color: #1e293b;
		  font-weight: 700;
		}

		.instagram-post-page .page-numbers button:disabled {
		  cursor: not-allowed;
		  opacity: 0.45;
		}

		.instagram-post-page .page-size-selector {
		  align-items: center;
		  color: var(--text-secondary);
		  display: flex;
		  font-size: 13px;
		  gap: 8px;
		}

		.instagram-post-page .empty-row {
		  color: var(--text-muted);
		  padding: 40px;
		  text-align: center;
		}

		.instagram-post-page .post-action-wrap {
		  align-items: center;
		  display: flex;
		  justify-content: center;
		}

		.instagram-post-page .post-delete-btn {
		  align-items: center;
		  background: #fff;
		  border: 1px solid #333;
		  border-radius: 6px;
		  color: #333;
		  cursor: pointer;
		  display: inline-flex;
		  font-size: 12px;
		  font-weight: 700;
		  gap: 5px;
		  justify-content: center;
		  min-width: 58px;
		  padding: 7px 10px;
		  transition: all 0.2s ease;
		}

		.instagram-post-page .post-delete-btn:hover {
		  border-color: var(--primary-mint) !important;
		  background: #f0fdfe !important;
		}

		@media screen and (max-width: 1024px) {
		  .instagram-post-page .content-inner {
			gap: 24px;
			padding: 24px 16px;
		  }
		}

		@media screen and (max-width: 800px) {
		  .instagram-post-page .center-hero {
			gap: 14px;
			margin-bottom: 8px;
		  }

		  .instagram-post-page .center-hero h2 {
			font-size: 22px;
		  }

		  .instagram-post-page .center-hero p {
			font-size: 13px;
			margin: 0;
			padding: 0 16px;
			word-break: keep-all;
		  }

		  .instagram-post-page .filter-controls-row {
			align-items: stretch;
			flex-direction: column;
		  }

		  .instagram-post-page .search-box-unified,
		  .instagram-post-page .filter-select-wrap {
			width: 100%;
		  }

		  .instagram-post-page .date-range-group {
			justify-content: space-between;
			min-width: 0;
			width: 100%;
		  }

		  .instagram-post-page .date-input {
			width: 100%;
		  }

		  .instagram-post-page .btn-apply {
			width: 100%;
		  }

		  .instagram-post-page .filter-chips-row {
			align-items: center;
			flex-direction: row;
			flex-wrap: nowrap;
			gap: 8px;
			justify-content: space-between;
		  }

		  .instagram-post-page .chip-group {
			flex-wrap: nowrap;
			overflow-x: auto;
			scrollbar-width: none;
		  }

		  .instagram-post-page .chip-group::-webkit-scrollbar {
			display: none;
		  }

		  .instagram-post-page .total-count {
			flex-shrink: 0;
			margin-left: auto;
		  }

		  /* 모바일에서는 컬럼 수가 많기 때문에 카드형으로 재배치하지 않고
			 데스크톱 표 구조를 그대로 유지한 채 가로 스크롤로 탐색한다. */
		  .instagram-post-page .table-responsive {
			display: block;
			overflow-x: auto;
			overflow-y: hidden;
			-webkit-overflow-scrolling: touch;
			overscroll-behavior-x: contain;
			width: 100%;
		  }

		  .instagram-post-page .robo-table {
			display: table;
			min-width: 2440px;
			width: 2440px;
			table-layout: fixed;
		  }

		  .instagram-post-page .robo-table thead {
			display: table-header-group;
		  }

		  .instagram-post-page .robo-table tbody {
			display: table-row-group;
		  }

		  .instagram-post-page .robo-table tr {
			display: table-row;
		  }

		  .instagram-post-page .robo-table th,
		  .instagram-post-page .robo-table td {
			display: table-cell;
			padding: 12px 10px;
			vertical-align: middle;
		  }

		  .instagram-post-page .robo-table th {
			font-size: 12px;
		  }

		  .instagram-post-page .robo-table td {
			font-size: 12px;
			text-align: center;
		  }

		  .instagram-post-page .robo-table .caption-text {
			max-width: 240px;
			white-space: normal;
			word-break: break-all;
		  }

		  .instagram-post-page .robo-table .user-cell {
			white-space: normal;
		  }

		  .instagram-post-page .robo-table .cell-account,
		  .instagram-post-page .robo-table .cell-date,
		  .instagram-post-page .robo-table .file-type-count {
			white-space: nowrap;
		  }

		  .instagram-post-page .thumb-img,
		  .instagram-post-page .thumb-media {
			height: 58px;
			width: 58px;
		  }

		  .instagram-post-page .engagement-box {
			flex-wrap: nowrap;
			justify-content: center;
			white-space: nowrap;
		  }

		  .instagram-post-page .table-responsive::-webkit-scrollbar {
			height: 8px;
		  }

		  .instagram-post-page .table-responsive::-webkit-scrollbar-thumb {
			background: #cbd5e1;
			border-radius: 999px;
		  }

		  .instagram-post-page .table-responsive::-webkit-scrollbar-track {
			background: #f8fafc;
		  }

		  .instagram-post-page .pagination-container {
			align-items: center;
			flex-direction: column;
			gap: 12px;
			justify-content: center;
			padding: 16px 12px;
		  }

		  .instagram-post-page .page-numbers {
			margin: 0;
		  }

		  .mobile-display-none {
			display: none;
		  }

		  // .instagram-post-page .page-size-selector {
		  //   display: none;
		  // }

		  .instagram-post-page #btnExcelDownload {
			width: 100% !important;
		  }
		}

		.instagram-post-page #btnExcelDownload {
			font-size: 13px;
			padding: 10px 24px;
			height: auto !important;
			border-radius: 6px;
		}
	  `}</style>
	);
  }


  render() {
	const { pageSizes } = this.props;
	const { q, startDate, endDate, page, pageSize, dateType, serviceUsers, user_seq } = this.state;
	const rows = this.state.posts;
	const total = this.state.total ?? rows.length;
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const userOptions = (serviceUsers || []).map((user) => ({
	  text: `${user.corp_name || user.user_id || "-"} (${user.user_id || "-"} / ${user.user_seq})`,
	  value: String(user.user_seq),
	}));

	return (
	  <PreviewRoot>
		<div id="page-wrapper" className="instagram-post-page admin-instagram-usage-page">
		  {this.renderListPageStyles()}
		  <style>{`
			.admin-instagram-usage-page .filter-user-wrap { width: 250px; min-width: 220px; }
			.admin-instagram-usage-page .excel-download-wrap { display:flex; align-items:center; flex-shrink:0; }
			.admin-instagram-usage-page .excel-download-wrap > * { margin:0 !important; }
			.admin-instagram-usage-page .user-cell { text-align:center; line-height:1.4; }
			.admin-instagram-usage-page .user-cell strong { display:block; color:var(--text-primary); font-size:13px; }
			.admin-instagram-usage-page .user-cell span { display:block; color:var(--text-muted); font-size:11px; margin-top:2px; }

			/* v2: Instagram / Threads 플랫폼별 현황 */
			.admin-instagram-usage-page .platform-stack { display:flex; flex-direction:column; gap:7px; min-width:150px; }
			.admin-instagram-usage-page .platform-row { display:flex; align-items:center; justify-content:flex-start; gap:7px; min-height:24px; }
			.admin-instagram-usage-page .platform-label {
			  display:inline-flex !important; align-items:center !important; justify-content:center !important; flex:0 0 68px; height:22px;
			  padding:0 7px; border:1px solid var(--border-color); border-radius:999px; background:#f8fafc;
			  color:var(--text-secondary); font-size:10px !important; font-weight:500 !important; white-space:nowrap;
			}
			.admin-instagram-usage-page .platform-label.instagram { color:#7c3aed; }
			.admin-instagram-usage-page .platform-label.threads { color:#111827; }
			.admin-instagram-usage-page .platform-account-value { min-width:0; color:var(--text-primary); font-size:12px; line-height:1.35; text-align:left; word-break:break-all; }
			.admin-instagram-usage-page .platform-account-value span { display:block; color:var(--text-muted); font-size:11px; margin-top:1px; }
			.admin-instagram-usage-page .platform-status-list { display:flex; flex-direction:column; gap:7px; min-width:142px; }
			.admin-instagram-usage-page .platform-status-row { display:flex; align-items:center; justify-content:flex-start; gap:7px; min-height:24px; }
			.admin-instagram-usage-page .platform-status-row .badge { min-width:54px; padding-left:8px; padding-right:8px; }
			.admin-instagram-usage-page .platform-engagement-list { display:flex; flex-direction:column; gap:7px; min-width:235px; }
			.admin-instagram-usage-page .platform-engagement-row { display:flex; align-items:center; justify-content:flex-start; gap:7px; min-height:24px; }
			.admin-instagram-usage-page .platform-engagement-row .engagement-box { justify-content:flex-start; gap:9px; font-size:12px; white-space:nowrap; }
			.admin-instagram-usage-page .platform-date-list { display:flex; flex-direction:column; gap:7px; min-width:118px; }
			.admin-instagram-usage-page .platform-date-row { display:flex; align-items:flex-start; justify-content:flex-start; gap:7px; }
			.admin-instagram-usage-page .platform-date-value { color:var(--text-primary); font-size:11px; line-height:1.35; text-align:left; white-space:nowrap; }
			.admin-instagram-usage-page .platform-date-value span { display:block; color:var(--text-muted); font-size:10px; margin-top:1px; }

			.admin-instagram-usage-page .source-type-list { display:flex; flex-wrap:wrap; gap:4px; justify-content:center; }
			.admin-instagram-usage-page .source-type-pill { display:inline-flex; padding:3px 7px; border-radius:999px; background:#f1f5f9; color:#475569; font-size:10px; font-weight:700; white-space:nowrap; }
			.admin-instagram-usage-page .file-type-count { text-align:center; font-weight:700; color:var(--text-primary); }
			.admin-instagram-usage-page .file-type-count.is-zero { color:#cbd5e1; font-weight:500; }
			.admin-instagram-usage-page .remain-expired { color:#9ca3af; }
			.admin-instagram-usage-page .remain-soon { color:#ef4444; font-weight:700; }
			.admin-instagram-usage-page .robo-table { min-width: 2440px; }
			@media screen and (max-width: 800px) {
			  .admin-instagram-usage-page { overflow-x: hidden; }
			  .admin-instagram-usage-page .content-inner { padding: 20px 12px; }
			  .admin-instagram-usage-page .filter-header { padding: 16px 12px; }
			  .admin-instagram-usage-page .filter-user-wrap { width: 100% !important; min-width: 0 !important; }
			  .admin-instagram-usage-page .excel-download-wrap { width: 100%; }
			  .admin-instagram-usage-page .excel-download-wrap > * { width: 100% !important; }
			  .admin-instagram-usage-page .date-range-group { gap: 6px; }
			  .admin-instagram-usage-page .date-picker-wrapper { min-width: 0; }
			  .admin-instagram-usage-page .date-input { min-width: 0; padding-left: 8px; padding-right: 8px; }
			  .admin-instagram-usage-page .list-section-wrapper { overflow: hidden; }
			  .admin-instagram-usage-page .table-responsive { border-top: 1px solid var(--border-color); }
			}
			
			.admin-instagram-usage-page .center-hero {
				align-items: flex-start;
			}
		  `}</style>

		  <main className="main-content">
			<div className="content-inner">
			  <section className="center-hero">
				<h2>[Admin] Instagram / Threads 이용 현황</h2>
				{/* <p>인스타그램 자동관리 신청 사용자의 피드 등록·배포 상태와 콘텐츠 제작 방식, 참여 통계를 확인합니다.</p> */}
			  </section>

			  <section className="list-section-wrapper">
				<div className="filter-header">
				  <div className="filter-controls-row">
					<div className="search-box-unified">
					  <i className="search-icon fa fa-solid fa-magnifying-glass"></i>
					  <TextInput
						type="text"
						id="search_q"
						placeholder="게시물 문구 검색..."
						value={q}
						onChange={(e, value) => this.setState({ q: value })}
						width="100%"
						height="38px"
						onKeyDown={(e) => {
						  if (e.key === "Enter") this.setState({ page: 1 }, this.fetchPostsFromApi);
						}}
					  />
					</div>

					<div className="filter-select-wrap filter-user-wrap">
					  <SelectBoxV2
						id="adminInstagramUserSelect"
						className="form-control"
						pageWrapperName="common-page-wrapper"
						width="100%"
						height="40px"
						padding="6px 30px 6px 10px"
						list={userOptions}
						optionTexttName="text"
						optionValuetName="value"
						selectedValue={String(user_seq || "")}
						placeHolder="전체 사용자"
						searchable={true}
						searchPlaceHolder="사용자 검색..."
						onClickOption={this.handleSelectBox("user_seq")}
						tabIndex={0}
					  />
					</div>

					<div className="filter-select-wrap">
					  <SelectBoxV2
						id="statusSelect"
						className="form-control"
						pageWrapperName="common-page-wrapper"
						width="100%"
						height="40px"
						padding="6px 30px 6px 10px"
						list={STATUS_OPTIONS}
						optionTexttName="text"
						optionValuetName="value"
						selectedValue={this.state.status}
						placeHolder="전체 상태"
						onClickOption={this.handleSelectBox("status")}
						tabIndex={0}
					  />
					</div>

					<div className="filter-select-wrap">
					  <SelectBoxV2
						id="typeSelect"
						className="form-control"
						pageWrapperName="common-page-wrapper"
						width="100%"
						height="40px"
						padding="6px 30px 6px 10px"
						list={TYPE_OPTIONS}
						optionTexttName="text"
						optionValuetName="value"
						selectedValue={this.state.is_auto}
						placeHolder="전체 유형"
						onClickOption={this.handleSelectBox("is_auto")}
						tabIndex={0}
					  />
					</div>

					<div className="date-range-group">
					  <div className="date-picker-wrapper">
						<DatePicker
						  selected={startDate}
						  onChange={(date) => this.setState({ startDate: date, page: 1, dateType: "custom" })}
						  selectsStart
						  startDate={startDate}
						  endDate={endDate}
						  maxDate={endDate || undefined}
						  dateFormat="yyyy.MM.dd"
						  locale={ko}
						  isClearable
						  placeholderText="등록 시작일"
						  customInput={<input className="date-input" />}
						/>
					  </div>
					  <span style={{ color: "var(--text-muted)" }}>~</span>
					  <div className="date-picker-wrapper">
						<DatePicker
						  selected={endDate}
						  onChange={(date) => this.setState({ endDate: date, page: 1, dateType: "custom" })}
						  selectsEnd
						  startDate={startDate}
						  endDate={endDate}
						  minDate={startDate || undefined}
						  dateFormat="yyyy.MM.dd"
						  locale={ko}
						  isClearable
						  placeholderText="등록 종료일"
						  customInput={<input className="date-input" />}
						/>
					  </div>
					</div>

					<button className="btn-apply" type="button" onClick={() => this.setState({ page: 1 }, this.fetchPostsFromApi)}>
					  필터 적용
					</button>
					<div className="excel-download-wrap">
					  <FileDownloadComponent
						url={this.getExcelDownloadUrl()}
						obj=""
						val1="none"
					  />
					</div>
				  </div>

				  <div className="filter-chips-row">
					<div className="chip-group">
					  <button type="button" className={"chip-btn" + (!dateType || dateType === "all" ? " active" : "")} onClick={() => this.setQuickRange("all")}>전체</button>
					  <button type="button" className={"chip-btn" + (dateType === "today" ? " active" : "")} onClick={() => this.setQuickRange("today")}>오늘</button>
					  <button type="button" className={"chip-btn" + (dateType === "week" ? " active" : "")} onClick={() => this.setQuickRange("week")}>이번 주</button>
					  <button type="button" className={"chip-btn" + (dateType === "month" ? " active" : "")} onClick={() => this.setQuickRange("month")}>이번 달</button>
					</div>
					<div className="total-count">총 <span>{this.fmtNumber(total)}</span>개 게시물</div>
				  </div>
				</div>

				<div className="table-responsive">
				  <table className="robo-table">
					<thead>
					  <tr>
						<th style={{ width: "55px" }}>No.</th>
						<th style={{ width: "80px" }}>이미지</th>
						<th style={{ width: "170px", textAlign: "center" }}>사용자</th>
						<th style={{ width: "95px" }}>가입일</th>
						<th style={{ width: "100px" }}>무료체험<br/>남은일</th>
						<th style={{ width: "95px" }}>유료<br/>남은일</th>
						{this.renderSortHead("피드 문구 (캡션)", "caption", "left", "240px")}
						{this.renderSortHead("피드 등록일", "reg_date", "center", "110px")}
						{this.renderSortHead("게시 일시", "published_time", "center", "150px")}
						<th style={{ width: "80px" }}>유형</th>
						{this.renderSortHead("참여 (좋아요/댓글/조회)", "total_cnt", "center", "245px")}
						<th style={{ width: "110px" }}>AI 이미지<br/>스튜디오</th>
						<th style={{ width: "95px" }}>AI 디자인</th>
						<th style={{ width: "95px" }}>파일 업로드</th>
						<th style={{ width: "90px" }}>상품 이미지</th>
						<th style={{ width: "75px" }}>동영상</th>
						<th style={{ width: "65px" }}>음악</th>
						<th style={{ width: "65px" }}>기타</th>
						<th style={{ width: "90px" }}>상태</th>
						<th style={{ width: "185px" }}>발행 계정</th>
						<th style={{ width: "80px" }}>배포 여부</th>
					  </tr>
					</thead>
					<tbody>
					  {rows.length === 0 ? (
						<tr><td colSpan={21} className="empty-row">조건에 맞는 게시물이 없습니다.</td></tr>
					  ) : (
						rows.map((row, rowIndex) => {
						  const instagramEngagement = (row.like_cnt ?? 0) + (row.comment_cnt ?? 0) + (row.view_cnt ?? 0);
						  const threadsEngagement = (row.threads_like_cnt ?? 0) + (row.threads_reply_cnt ?? 0) + (row.threads_view_cnt ?? 0);
						  const showInstagram = row.instagramPublishYn !== "N";
						  const showThreads = row.threadsPublishYn === "Y";
						  const threadsStatusCd = String(row.threadsPublishStatusCd || "").toUpperCase();
						  const instagramPublishText = !showInstagram
							? "미선택"
							: row.status === "게시됨" ? "배포" : row.status === "실패" ? "실패" : "미배포";
						  const threadsPublishText = !showThreads
							? "미선택"
							: ["PUBLISHED", "SUCCESS"].includes(threadsStatusCd) ? "배포"
							: threadsStatusCd === "FAILED" ? "실패"
							: threadsStatusCd === "RUNNING" ? "진행중" : "미배포";
						  const platformStatusClass = (text) =>
							text === "배포" ? "badge-blue" : text === "실패" ? "badge-red" : text === "진행중" ? "badge-yellow" : "badge-gray";
						  const statusClass =
							row.status === "게시됨" ? "badge-blue" :
							row.status === "예약됨" ? "badge-gray" :
							row.status === "임시저장" ? "badge-yellow" :
							row.status === "실패" ? "badge-red" : "badge-gray";
						  const freeDays = Number(row.freeRemainDate);
						  const payDays = Number(row.payRemainDate);
						  const freeClass = row.freeRemainDate !== null && row.freeRemainDate !== undefined
							? (freeDays < 0 ? "remain-expired" : freeDays <= 7 ? "remain-soon" : "") : "";
						  const payClass = row.payRemainDate !== null && row.payRemainDate !== undefined
							? (payDays < 0 ? "remain-expired" : payDays <= 7 ? "remain-soon" : "") : "";

						  return (
							<tr key={row.id}>
							  <td data-label="No." className="list-no">{total - ((page - 1) * pageSize + rowIndex)}</td>
							  <td data-label="이미지">
								<PreviewTrigger
								  align="right"
								  previewData={{
									slides: row.slides,
									caption: row.caption,
									hashtags: row.hashtags,
									publishedAt: row.publishedDate && row.publishedTime ? `${row.publishedDate} ${row.publishedTime}` : null,
									metrics: { likes: row.like_cnt, comments: row.comment_cnt, saves: row.view_cnt },
									instagramPublishYn: row.instagramPublishYn,
									threadsPublishYn: row.threadsPublishYn,
									threadsMetrics: {
									  likes: row.threads_like_cnt,
									  replies: row.threads_reply_cnt,
									  views: row.threads_view_cnt,
									},
									image_ratio: row.image_ratio,
									reelsAudio: row.reelsAudio,
								  }}
								>
								  {row.thumbnail && !this.looksLikeVideoUrl(row.thumbnail) ? (
									<div className="thumb-media">
									  <img src={row.thumbnail} alt="thumbnail" />
									  {row.isVideoPost && <><div className="thumb-video-dim"></div><div className="thumb-play-icon"><span></span></div></>}
									  {row.carouselCount ? <div className="thumb-count-badge">+{row.carouselCount}</div> : null}
									</div>
								  ) : (
									<div className="thumb-media thumb-placeholder">
									  {row.isVideoPost && ((row.slides || []).find((s) => s?.type === "video" && s.url) ? (
										<>
										  <video
											src={(row.slides || []).find((s) => s?.type === "video" && s.url)?.url || ""}
											muted playsInline preload="metadata"
											style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", background:"#111827" }}
										  />
										  <div className="thumb-video-dim"></div><div className="thumb-play-icon"><span></span></div>
										</>
									  ) : <div className="thumb-play-icon"><span></span></div>)}
									</div>
								  )}
								</PreviewTrigger>
							  </td>
							  <td data-label="사용자" className="user-cell">
								<strong>{row.corpName || row.userId || `user_seq ${row.userSeq}`}</strong>
								<span>{row.userId || "-"} / {row.userSeq}</span>
							  </td>
							  <td data-label="가입일" className="cell-date">{row.userRegDate || "-"}</td>
							  <td data-label="무료체험 남은일" className={freeClass}>{this.formatRemainDays(row.freeRemainDate, "무료체험만료")}</td>
							  <td data-label="유료 남은일" className={payClass}>{this.formatRemainDays(row.payRemainDate)}</td>
							  <td data-label="문구" className="text-left caption-text">{row.caption || "-"}</td>
							  <td data-label="피드 등록일" className="cell-date">{row.regDate || "-"}<br/><span>{row.regTime || ""}</span></td>
							  <td data-label="게시 일시" className="cell-date">
								<div className="platform-date-list">
								  {showInstagram && (
									<div className="platform-date-row">
									  <span className="platform-label instagram">Instagram</span>
									  <div className="platform-date-value">{row.publishedDate || "-"}<span>{row.publishedTime || ""}</span></div>
									</div>
								  )}
								  {showThreads && (
									<div className="platform-date-row">
									  <span className="platform-label threads">Threads</span>
									  <div className="platform-date-value">{row.threadsPublishedDate || "-"}<span>{row.threadsPublishedTime || ""}</span></div>
									</div>
								  )}
								  {!showInstagram && !showThreads ? <span>-</span> : null}
								</div>
							  </td>
							  <td data-label="유형"><span className={"badge " + (row.isAuto === "Y" ? "badge-mint" : "badge-gray")}>{row.isAutoText || "-"}</span></td>
							  <td data-label="참여">
								<div className="platform-engagement-list">
								  {showInstagram && (
									<div className="platform-engagement-row">
									  <span className="platform-label instagram">Instagram</span>
									  <div className={"engagement-box" + (instagramEngagement > 0 ? " active" : "")}>
										<div className="engagement-item"><span className="engagement-heart fa fa-heart"></span> {this.fmtNumber(row.like_cnt)}</div>
										<div className="engagement-item"><span className="engagement-comment fa fa-comment"></span> {this.fmtNumber(row.comment_cnt)}</div>
										<div className="engagement-item"><span className="engagement-view fa fa-eye"></span> {this.fmtNumber(row.view_cnt)}</div>
									  </div>
									</div>
								  )}
								  {showThreads && (
									<div className="platform-engagement-row">
									  <span className="platform-label threads">Threads</span>
									  <div className={"engagement-box" + (threadsEngagement > 0 ? " active" : "")}>
										<div className="engagement-item"><span className="engagement-heart fa fa-heart"></span> {this.fmtNumber(row.threads_like_cnt)}</div>
										<div className="engagement-item"><span className="engagement-comment fa fa-comment"></span> {this.fmtNumber(row.threads_reply_cnt)}</div>
										<div className="engagement-item"><span className="engagement-view fa fa-eye"></span> {this.fmtNumber(row.threads_view_cnt)}</div>
									  </div>
									</div>
								  )}
								  {!showInstagram && !showThreads ? <span>-</span> : null}
								</div>
							  </td>
							  <td data-label="AI 이미지 스튜디오" className={"file-type-count" + ((row.fileTypeCounts?.aiImageStudio || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.aiImageStudio || 0}</td>
							  <td data-label="AI 디자인" className={"file-type-count" + ((row.fileTypeCounts?.aiDesign || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.aiDesign || 0}</td>
							  <td data-label="파일 업로드" className={"file-type-count" + ((row.fileTypeCounts?.upload || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.upload || 0}</td>
							  <td data-label="상품 이미지" className={"file-type-count" + ((row.fileTypeCounts?.product || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.product || 0}</td>
							  <td data-label="동영상" className={"file-type-count" + ((row.fileTypeCounts?.video || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.video || 0}</td>
							  <td data-label="음악" className={"file-type-count" + ((row.musicCount || 0) === 0 ? " is-zero" : "")}>{row.musicCount || 0}</td>
							  <td data-label="기타" className={"file-type-count" + ((row.fileTypeCounts?.other || 0) === 0 ? " is-zero" : "")}>{row.fileTypeCounts?.other || 0}</td>
							  <td data-label="상태"><span className={"badge " + statusClass}>{row.status || "-"}</span></td>
							  <td data-label="발행 계정" className="cell-account">
								<div className="platform-stack">
								  {showInstagram && (
									<div className="platform-row">
									  <span className="platform-label instagram">Instagram</span>
									  <div className="platform-account-value">
										{row.facebook_username || row.username || "-"}
										{row.facebook_username && row.username ? <span>@{row.username}</span> : null}
									  </div>
									</div>
								  )}
								  {showThreads && (
									<div className="platform-row">
									  <span className="platform-label threads">Threads</span>
									  <div className="platform-account-value">
										{row.threadsUsername ? `@${String(row.threadsUsername).replace(/^@/, "")}` : "-"}
									  </div>
									</div>
								  )}
								  {!showInstagram && !showThreads ? <span>-</span> : null}
								</div>
							  </td>
							  <td data-label="배포 여부">
								<div className="platform-status-list">
								  {showInstagram && (
									<div className="platform-status-row">
									  <span className="platform-label instagram">Instagram</span>
									  <span className={"badge " + platformStatusClass(instagramPublishText)}>{instagramPublishText}</span>
									</div>
								  )}
								  {showThreads && (
									<div className="platform-status-row">
									  <span className="platform-label threads">Threads</span>
									  <span className={"badge " + platformStatusClass(threadsPublishText)}>{threadsPublishText}</span>
									</div>
								  )}
								  {!showInstagram && !showThreads ? <span className="badge badge-gray">미선택</span> : null}
								</div>
							  </td>
							</tr>
						  );
						})
					  )}
					</tbody>
				  </table>
				</div>

				<div className="pagination-container">
				  <div className="mobile-display-none"></div>
				  <div className="page-numbers">
					<button type="button" disabled={page <= 1} onClick={() => this.goPage(page - 1)}><i className="fa fa-solid fa-caret-left f-s-13"></i></button>
					{Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
					  const half = Math.floor(5 / 2);
					  let start = Math.max(1, page - half);
					  let end = Math.min(totalPages, start + 4);
					  if (end - start < 4) start = Math.max(1, end - 4);
					  const p = start + i;
					  if (p > totalPages) return null;
					  return <button key={p} type="button" className={p === page ? "active" : ""} onClick={() => this.goPage(p)}>{p}</button>;
					})}
					<button type="button" disabled={page >= totalPages} onClick={() => this.goPage(page + 1)}><i className="fa fa-solid fa-caret-right f-s-13"></i></button>
				  </div>
				  <div className="page-size-selector">
					<span>페이지당</span>
					<SelectBoxV2
					  id="pageSizeSelect" className="form-control" pageWrapperName="common-page-wrapper"
					  width="120px" height="32px" padding="6px 30px 6px 10px"
					  list={pageSizes} optionTexttName="text" optionValuetName="value"
					  selectedValue={this.state.pageSize} placeHolder="페이지 크기"
					  onClickOption={(text, value) => this.changePageSize({ target: { value } })} tabIndex={0}
					/>
				  </div>
				</div>
			  </section>
			</div>
		  </main>
		</div>
	  </PreviewRoot>
	);
  }
}
